import { type Durability } from '@d2/core';
import { isCharacterClass, type CharacterClass, type CharacterSummary } from '@d2/schema/characters';

/**
 * Everything this client says to `apps/api`, and nothing else.
 *
 * The contract is not guessed: every path, body and status below is one
 * `apps/api/src/auth/auth.e2e.test.ts` and `characters.e2e.test.ts` assert the
 * API produces. Two of them are load-bearing and easy to get subtly wrong.
 *
 * **Unknown keys fail a body.** `parseRegisterBody` and
 * `parseCreateCharacterBody` refuse any field nobody asked for, so a client that
 * helpfully sent `{…, remember: true}` would have every registration answered
 * 400. The request builders here therefore construct exactly the declared object
 * rather than spreading a form state into one.
 *
 * **A 401 means two different things and they are different screens.** On
 * `login` it is *those credentials are not right* — a form error, with the
 * player still exactly as signed-out as they were. On a guarded route it is
 * *the session this client was holding is over*, which is a sign-in screen and a
 * cleared token. Nothing else can tell them apart, because the API answers both
 * with one message on purpose.
 *
 * ## Nothing here logs, and no refusal carries a credential
 *
 * There is no `console` call in this file and there never may be: the two values
 * that pass through it are a password and a token, and a log line is the one
 * place either would outlive the request. Refusals are built from the *status*
 * and from the API's own `{field, reason}` — never from the value that was
 * refused, which is the same rule `apps/api`'s `badBody` keeps on the other
 * side.
 *
 * ## Every failure is a returned result
 *
 * A network that is down, an API that answers something unexpected and a
 * password that is wrong are all ordinary outcomes of asking (`CLAUDE.md`), so
 * nothing here throws. The one thing a caller must never have to do is wrap a
 * call in a `try`, because a caller that forgets takes the whole page down with
 * an unhandled rejection.
 */

/**
 * Where the API is when the build was not told otherwise.
 *
 * Same origin, because `deploy/nginx/nl_ronaldswets_d2.conf` proxies `/api/` to
 * the API container and everything else to this one. A same-origin path is also
 * what keeps the browser from ever needing CORS — and what would let the token
 * move to an `httpOnly` cookie without changing a line here (`TODO.md` #391).
 */
export const DEFAULT_API_BASE = '/api';

/** The public shape of an account, exactly as `AuthService.viewOf` builds it. */
export interface AccountView {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly createdAt: number;
}

/** What a register or a login answers with. */
export interface AuthenticatedSession {
  readonly account: AccountView;
  readonly token: string;
}

/**
 * Every reason the vendor gives for not doing something, exactly as
 * `VendorController` puts it on a 409.
 *
 * They are **restated here rather than imported**, for the reason
 * `game/join-refusal.ts` restates three close codes: `apps/web` does not depend
 * on `apps/api`, and the two are separate containers. That is a real cost, and
 * what pays it back is that this list is *closed*: a reason outside it is a
 * contract that moved, and a client that passed the API's own word through to a
 * player would put `insufficient-mana` on a town screen the day one appeared.
 *
 * Every one of them is an **ordinary outcome the player caused** — a full bag, an
 * empty purse, a thing that does not wear — bar `conflict`, which is somebody
 * else writing the same character mid-exchange and whose honest answer is to ask
 * again. `ui/vendor-model.ts` owns the sentence each of them becomes.
 */
export const VENDOR_REFUSALS = Object.freeze([
  'insufficient-gold',
  'inventory-full',
  'purse-full',
  'no-such-item',
  'unknown-offer',
  'nothing-to-repair',
  'not-repairable',
  'conflict',
] as const);

export type VendorRefusalReason = (typeof VENDOR_REFUSALS)[number];

const isVendorRefusalReason = (value: unknown): value is VendorRefusalReason =>
  typeof value === 'string' && VENDOR_REFUSALS.some((reason) => reason === value);

/**
 * Why the API would not do something.
 *
 * One kind per outcome a screen answers *differently*. `invalid-credentials` and
 * `unauthenticated` are both a 401 and are deliberately not merged: the first is
 * a form that was filled in wrongly, the second is a session that has ended.
 */
export type ApiRefusal =
  /** The API named a field and a reason. Never a value. */
  | { readonly kind: 'invalid-field'; readonly field: string; readonly reason: string }
  /** The vendor looked at the character and said no, for a reason it named. */
  | { readonly kind: 'vendor-refused'; readonly reason: VendorRefusalReason }
  /** A login that was refused. Which half was wrong is not knowable, by design. */
  | { readonly kind: 'invalid-credentials' }
  /** Registration's one admitted enumeration oracle (`TODO.md` #390). */
  | { readonly kind: 'email-unavailable' }
  | { readonly kind: 'name-taken' }
  | { readonly kind: 'too-many-characters' }
  /** A character that is not there — which is also what somebody else's answers. */
  | { readonly kind: 'no-such-character' }
  | { readonly kind: 'rate-limited'; readonly retryAfterSeconds: number | null }
  /** The token this call carried is no longer accepted. */
  | { readonly kind: 'unauthenticated' }
  /** No answer, or one this client cannot make sense of. */
  | { readonly kind: 'unreachable' };

export type ApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly refusal: ApiRefusal };

export interface RegisterCredentials {
  readonly email: string;
  readonly password: string;
  readonly displayName: string;
}

export interface LoginCredentials {
  readonly email: string;
  readonly password: string;
}

export interface NewCharacter {
  readonly name: string;
  readonly characterClass: CharacterClass;
}

/**
 * One thing in the shop window, as much of it as a counter draws.
 *
 * The id, the name and the price, and deliberately not the whole `VendorOffer`
 * core mints. This client used to decode the goods entire — every modifier,
 * every affix, the value and the wear state — because it *priced* them; it does
 * not any more, and a decoder that went on validating fields nobody reads would
 * be a second, drifting copy of `Item` kept for no reader's benefit.
 */
export interface VendorWindowOffer {
  /** The catalogue id a purchase names. Never the id the bought copy ends up with. */
  readonly offerId: string;
  readonly name: string;
  /** What the vendor asks, whole, exactly as the API stated it. */
  readonly price: number;
}

/**
 * Where the vendor found a thing. The three containers it can reach.
 *
 * **Restated here rather than imported**, for the reason `VENDOR_REFUSALS` above
 * is: `apps/web` does not depend on `apps/api`, and the two are separate
 * containers. Closed on purpose — a container outside this list is a contract
 * that moved, and the panel's one real decision (*"the stash is quoted and not
 * offered"*) turns on being able to tell them apart.
 */
export const QUOTED_LOCATIONS = Object.freeze(['equipment', 'inventory', 'stash'] as const);

export type QuotedLocation = (typeof QUOTED_LOCATIONS)[number];

/**
 * One thing this character is carrying, at the two figures an exchange over it
 * would come to — as `GET /api/characters/:id/vendor/quote` states them.
 *
 * Both figures are nullable and **both nulls are refusals the server would give**,
 * not gaps: `sale: null` is worn gear the vendor will not buy, and `repair: null`
 * is a thing that is whole or never wears. Reading either as a zero would be this
 * client inventing a price for an exchange that will be refused, which is the
 * whole class of rule the quote route exists to take out of it.
 */
export interface QuotedThing {
  readonly itemId: string;
  readonly name: string;
  readonly in: QuotedLocation;
  /** What the vendor would pay, or `null` because it would not buy it at all. */
  readonly sale: number | null;
  /** What making it whole would cost, or `null` because there is nothing to charge for. */
  readonly repair: number | null;
  /** What is left and what it had. Data off the item, not a rule about it. */
  readonly durability: Durability | null;
}

/**
 * What a purchase or a repair came to, and what the vendor now says about
 * everything else.
 *
 * `spent` and `received` are named for their **direction**, exactly as the API
 * names them, because *"sold for 137"* and *"repaired for 137"* read two
 * different facts and a signless number gets used backwards once and never
 * noticed (`vendor.controller.ts`).
 *
 * `quote` is the *other* kind of number and the two are never interchangeable:
 * the figure is what this exchange **did** cost and the quote is what the next
 * one **would**. It rides on the answer for the same reason `character` does —
 * the reply to the request that changed something is the earliest statement of
 * what it now is, and a client that asked again would be drawing one counter out
 * of two answers taken at two moments.
 */
export interface Purchase {
  /** The character as the server now has it. Town's purse is redrawn from this. */
  readonly character: CharacterSummary;
  /** What it cost, as the API charged it. Never a price this client quoted. */
  readonly spent: number;
  readonly quote: readonly QuotedThing[];
}

export interface Sale {
  readonly character: CharacterSummary;
  /** What it fetched, as the API paid it. */
  readonly received: number;
  readonly quote: readonly QuotedThing[];
}

export interface AccountApi {
  readonly register: (credentials: RegisterCredentials) => Promise<ApiResult<AuthenticatedSession>>;
  readonly logIn: (credentials: LoginCredentials) => Promise<ApiResult<AuthenticatedSession>>;
  /** Who a token names — the one call that says whether a stored session is still live. */
  readonly whoAmI: (token: string) => Promise<ApiResult<AccountView>>;
  readonly listCharacters: (token: string) => Promise<ApiResult<readonly CharacterSummary[]>>;
  readonly createCharacter: (token: string, character: NewCharacter) => Promise<ApiResult<CharacterSummary>>;
  readonly deleteCharacter: (token: string, characterId: string) => Promise<ApiResult<null>>;
  /** What this character may buy, and what each thing costs. Every price is the API's. */
  readonly readVendorStock: (token: string, characterId: string) => Promise<ApiResult<readonly VendorWindowOffer[]>>;
  /** What the vendor would pay for this character's own things, and what mending them costs. */
  readonly readVendorQuote: (token: string, characterId: string) => Promise<ApiResult<readonly QuotedThing[]>>;
  readonly buyFromVendor: (token: string, characterId: string, offerId: string) => Promise<ApiResult<Purchase>>;
  readonly sellToVendor: (token: string, characterId: string, itemId: string) => Promise<ApiResult<Sale>>;
  readonly repairAtVendor: (token: string, characterId: string, itemId: string) => Promise<ApiResult<Purchase>>;
}

export interface AccountApiOptions {
  /** Injected so the unit suite can drive one without a socket; a browser passes nothing. */
  readonly fetch?: typeof globalThis.fetch;
  readonly baseUrl?: string;
}

/** What came back, once. `null` is "nothing came back", which is a refusal all of its own. */
interface Answer {
  readonly status: number;
  /** `undefined` when the body was absent or not JSON — both of which are only ever a failure. */
  readonly body: unknown;
  readonly retryAfterSeconds: number | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * `Retry-After` as a count of seconds, or `null`.
 *
 * RFC 9110 allows an HTTP-date as well, and this deliberately does not read one:
 * a date has to be interpreted against a clock that may not agree with the
 * server's, and the only thing the number is used for is a sentence telling a
 * player roughly how long to wait.
 */
const retryAfterOf = (header: string | null): number | null => {
  if (header === null) return null;

  const seconds = Number(header);

  return Number.isInteger(seconds) && seconds >= 0 ? seconds : null;
};

const jsonOf = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    // A 204, or a proxy's HTML error page wearing a 200.
    return undefined;
  }
};

/** The `{field, reason}` an API refusal carries, or `null` when it carried none. */
const refusedField = (body: unknown): { readonly field: string; readonly reason: string } | null => {
  if (!isRecord(body)) return null;

  const field = body['field'];
  const reason = body['reason'];

  return typeof field === 'string' && typeof reason === 'string' ? { field, reason } : null;
};

const accountOf = (value: unknown): AccountView | null => {
  if (!isRecord(value)) return null;

  const { id, email, displayName, createdAt } = value;
  if (typeof id !== 'string' || typeof email !== 'string') return null;
  if (typeof displayName !== 'string' || typeof createdAt !== 'number') return null;

  return { id, email, displayName, createdAt };
};

const characterOf = (value: unknown): CharacterSummary | null => {
  if (!isRecord(value)) return null;

  const { id, accountId, name, characterClass, level, gold, experience, createdAt } = value;
  if (typeof id !== 'string' || typeof accountId !== 'string' || typeof name !== 'string') return null;
  if (!isCharacterClass(characterClass)) return null;
  if (typeof level !== 'number' || typeof gold !== 'number' || typeof createdAt !== 'number') return null;
  // Refused rather than defaulted to 0. A missing total would draw an
  // experience bar reading "nothing earned this level", which is a lie about
  // a character rather than a gap — and this decoder's whole job is to refuse
  // a body it cannot read.
  if (typeof experience !== 'number') return null;

  return { id, accountId, name, characterClass, level, gold, experience, createdAt };
};

/** The same narrowing `isRecord` does, without narrowing the caller's value. */
const recordOf = (value: unknown): Record<string, unknown> | null => (isRecord(value) ? value : null);

const wearStateOf = (value: unknown): Durability | null => {
  const fields = recordOf(value);
  if (fields === null) return null;

  const { current, maximum } = fields;

  return typeof current === 'number' && typeof maximum === 'number' ? { current, maximum } : null;
};

/** A whole figure the API stated, or `null` because it stated none. Never a default. */
const figureOf = (value: unknown): number | null | undefined => {
  if (value === null) return null;

  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

const isQuotedLocation = (value: unknown): value is QuotedLocation =>
  typeof value === 'string' && QUOTED_LOCATIONS.some((location) => location === value);

/**
 * One quoted thing, off the wire.
 *
 * **A missing figure fails the row; it is never defaulted.** That is the whole
 * discipline of this decoder and the reason it is written with an explicit
 * `undefined` in the middle of it: `null` is a refusal the server stated on
 * purpose, and anything else is a body this client cannot read. A `?? 0` here
 * would be the pricing rule creeping back in through the one door nobody
 * watches — a panel drawing a bid of nothing beside a Sell button.
 */
const quotedOf = (value: unknown): QuotedThing | null => {
  const fields = recordOf(value);
  if (fields === null) return null;

  const { itemId, name, durability } = fields;
  if (typeof itemId !== 'string' || itemId.length === 0 || typeof name !== 'string') return null;
  if (!isQuotedLocation(fields['in'])) return null;

  const sale = figureOf(fields['sale']);
  const repair = figureOf(fields['repair']);
  if (sale === undefined || repair === undefined) return null;
  // Absent is not a wear state either: a thing that never wears says `null`.
  if (durability !== null && wearStateOf(durability) === null) return null;

  return { itemId, name, in: fields['in'], sale, repair, durability: wearStateOf(durability) };
};

/** Every quoted thing, or `null` when one of them could not be read. */
const quoteOf = (body: unknown): readonly QuotedThing[] | null => {
  const listed = recordOf(body)?.['quote'];
  if (!Array.isArray(listed)) return null;

  const rows = listed.map(quotedOf);

  // One unreadable row fails the whole quote, for the reason one unreadable row
  // fails the whole roster: a counter silently short by one is a player who
  // thinks an item vanished.
  return rows.includes(null) ? null : (rows as readonly QuotedThing[]);
};

/** One thing in the shop window, at the price the API stated for it. */
const offerOf = (value: unknown): VendorWindowOffer | null => {
  const goods = recordOf(recordOf(value)?.['goods']);
  const price = recordOf(value)?.['price'];
  if (goods === null) return null;

  const { id, name } = goods;
  if (typeof id !== 'string' || id.length === 0 || typeof name !== 'string') return null;
  // No floor, no rounding and no multiplier: the price is the API's, whole, and
  // a client that adjusted one would be quoting a number nobody will charge.
  if (typeof price !== 'number' || !Number.isFinite(price)) return null;

  return { offerId: id, name, price };
};

const sessionOf = (body: unknown): AuthenticatedSession | null => {
  if (!isRecord(body)) return null;

  const account = accountOf(body['account']);
  const token = body['token'];

  return account === null || typeof token !== 'string' || token.length === 0 ? null : { account, token };
};

const unreachable = <T>(): ApiResult<T> => ({ ok: false, refusal: { kind: 'unreachable' } });

/** A body that arrived over a network and turned out not to be what it claimed. */
const decoded = <T>(value: T | null): ApiResult<T> => (value === null ? unreachable<T>() : { ok: true, value });

export const createAccountApi = ({ fetch = globalThis.fetch, baseUrl = DEFAULT_API_BASE }: AccountApiOptions): AccountApi => {
  const root = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

  const send = async (path: string, init: RequestInit, token?: string): Promise<Answer | null> => {
    const headers = new Headers({ accept: 'application/json' });

    if (init.body !== undefined) headers.set('content-type', 'application/json');
    // Built here rather than by a caller, so there is exactly one place a token
    // is ever put on a request — and exactly one thing to read when asking
    // whether it can leak anywhere else.
    if (token !== undefined) headers.set('authorization', `Bearer ${token}`);

    let response: Response;
    try {
      response = await fetch(`${root}${path}`, { ...init, headers });
    } catch {
      // A DNS failure, a refused connection, an aborted navigation. All the
      // same thing to a player: the game is not answering.
      return null;
    }

    return {
      status: response.status,
      body: await jsonOf(response),
      retryAfterSeconds: retryAfterOf(response.headers.get('retry-after')),
    };
  };

  /**
   * The statuses every route answers the same way.
   *
   * `authenticated` is what makes a 401 mean two things: a route that carried a
   * token has *lost a session*, and one that did not was a login attempt.
   */
  const common = (answer: Answer, authenticated: boolean): ApiRefusal | null => {
    if (answer.status === 429) return { kind: 'rate-limited', retryAfterSeconds: answer.retryAfterSeconds };
    if (answer.status === 401) return authenticated ? { kind: 'unauthenticated' } : { kind: 'invalid-credentials' };
    if (answer.status === 404) return { kind: 'no-such-character' };
    if (answer.status === 400) {
      const refused = refusedField(answer.body);

      return refused === null
        ? { kind: 'invalid-field', field: 'body', reason: 'malformed' }
        : { kind: 'invalid-field', field: refused.field, reason: refused.reason };
    }

    return null;
  };

  const post = (path: string, body: object, token?: string): Promise<Answer | null> =>
    send(path, { method: 'POST', body: JSON.stringify(body) }, token);

  /**
   * A character's own path. Encoded, so an id carrying a slash or a `..` is one
   * path segment and never a route of its own.
   */
  const characterPath = (characterId: string, suffix = ''): string =>
    `/characters/${encodeURIComponent(characterId)}${suffix}`;

  /**
   * What a vendor route answered that was not the exchange.
   *
   * The 409 is the interesting one and it is the *only* status that carries a
   * `reason`: it is the vendor saying no to a well-formed request, which is an
   * ordinary outcome a player caused and the one thing a town screen must be
   * able to put into words. A 422 deliberately does not land here — it is a bug
   * on the far side rather than the player's doing (`vendor.controller.ts`), and
   * there is nothing for a player to do about it that "the vendor is not
   * answering" does not already say.
   */
  const vendorRefusal = (answer: Answer): ApiRefusal => {
    if (answer.status === 409) {
      const reason = recordOf(answer.body)?.['reason'];

      if (isVendorRefusalReason(reason)) return { kind: 'vendor-refused', reason };
    }

    return common(answer, true) ?? { kind: 'unreachable' };
  };

  /**
   * One exchange, decoded: the character as it now stands, the figure under its
   * own name, and the fresh quote.
   *
   * All three or none. An answer whose quote cannot be read is refused rather
   * than half-used, because the half that *would* be used is the purse: a fresh
   * balance beside the counter that was on screen before the exchange is the one
   * combination in which the panel shows figures about a character that no
   * longer exists, and says nothing about it.
   */
  const exchanged = <T>(
    answer: Answer | null,
    figure: 'spent' | 'received',
    build: (character: CharacterSummary, quote: readonly QuotedThing[], gold: number) => T,
  ): ApiResult<T> => {
    if (answer === null) return unreachable();

    if (answer.status === 200) {
      const character = characterOf(recordOf(answer.body)?.['character']);
      const quote = quoteOf(answer.body);
      const gold = recordOf(answer.body)?.[figure];
      if (character === null || quote === null) return unreachable();
      if (typeof gold !== 'number' || !Number.isFinite(gold)) return unreachable();

      return { ok: true, value: build(character, quote, gold) };
    }

    return { ok: false, refusal: vendorRefusal(answer) };
  };

  return {
    register: async (credentials: RegisterCredentials): Promise<ApiResult<AuthenticatedSession>> => {
      // Written out field by field: `parseRegisterBody` refuses a body carrying
      // anything it did not declare, so spreading a form in here would 400.
      const answer = await post('/auth/register', {
        email: credentials.email,
        password: credentials.password,
        displayName: credentials.displayName,
      });
      if (answer === null) return unreachable();

      if (answer.status === 201) return decoded(sessionOf(answer.body));

      const refusal = common(answer, false);
      if (refusal === null) return unreachable();

      // A taken address arrives as a 400 on the email field, exactly like a
      // malformed one, and only the reason separates them. It is the one
      // refusal registration will admit to (`TODO.md` #390) and the only one
      // worth answering with "sign in instead".
      if (refusal.kind === 'invalid-field' && refusal.field === 'email' && refusal.reason === 'unavailable') {
        return { ok: false, refusal: { kind: 'email-unavailable' } };
      }

      return { ok: false, refusal };
    },

    logIn: async (credentials: LoginCredentials): Promise<ApiResult<AuthenticatedSession>> => {
      const answer = await post('/auth/login', { email: credentials.email, password: credentials.password });
      if (answer === null) return unreachable();

      if (answer.status === 200) return decoded(sessionOf(answer.body));

      const refusal = common(answer, false);

      return refusal === null ? unreachable() : { ok: false, refusal };
    },

    whoAmI: async (token: string): Promise<ApiResult<AccountView>> => {
      const answer = await send('/auth/me', { method: 'GET' }, token);
      if (answer === null) return unreachable();

      if (answer.status === 200) {
        return decoded(isRecord(answer.body) ? accountOf(answer.body['account']) : null);
      }

      const refusal = common(answer, true);

      return refusal === null ? unreachable() : { ok: false, refusal };
    },

    listCharacters: async (token: string): Promise<ApiResult<readonly CharacterSummary[]>> => {
      const answer = await send('/characters', { method: 'GET' }, token);
      if (answer === null) return unreachable();

      if (answer.status === 200) {
        const listed = isRecord(answer.body) ? answer.body['characters'] : undefined;
        if (!Array.isArray(listed)) return unreachable();

        const characters = listed.map(characterOf);

        // One unreadable entry fails the whole list rather than being dropped:
        // a roster silently short by one is a player who thinks a character was
        // deleted.
        return characters.includes(null) ? unreachable() : { ok: true, value: characters as readonly CharacterSummary[] };
      }

      const refusal = common(answer, true);

      return refusal === null ? unreachable() : { ok: false, refusal };
    },

    createCharacter: async (token: string, character: NewCharacter): Promise<ApiResult<CharacterSummary>> => {
      const answer = await post('/characters', { name: character.name, characterClass: character.characterClass }, token);
      if (answer === null) return unreachable();

      if (answer.status === 201) {
        return decoded(isRecord(answer.body) ? characterOf(answer.body['character']) : null);
      }

      if (answer.status === 409) {
        const refused = refusedField(answer.body);
        if (refused?.reason === 'taken') return { ok: false, refusal: { kind: 'name-taken' } };
        if (refused?.reason === 'too-many') return { ok: false, refusal: { kind: 'too-many-characters' } };

        return unreachable();
      }

      const refusal = common(answer, true);

      return refusal === null ? unreachable() : { ok: false, refusal };
    },

    deleteCharacter: async (token: string, characterId: string): Promise<ApiResult<null>> => {
      // Encoded, so an id carrying a slash or a `..` is one path segment and
      // never a route of its own — the same reasoning `internalSnapshotPath`
      // states on the server side.
      const answer = await send(characterPath(characterId), { method: 'DELETE' }, token);
      if (answer === null) return unreachable();

      if (answer.status === 204) return { ok: true, value: null };

      const refusal = common(answer, true);

      return refusal === null ? unreachable() : { ok: false, refusal };
    },

    readVendorQuote: async (token: string, characterId: string): Promise<ApiResult<readonly QuotedThing[]>> => {
      const answer = await send(characterPath(characterId, '/vendor/quote'), { method: 'GET' }, token);
      if (answer === null) return unreachable();

      if (answer.status === 200) return decoded(quoteOf(answer.body));

      const refusal = common(answer, true);

      return refusal === null ? unreachable() : { ok: false, refusal };
    },

    readVendorStock: async (token: string, characterId: string): Promise<ApiResult<readonly VendorWindowOffer[]>> => {
      const answer = await send(characterPath(characterId, '/vendor'), { method: 'GET' }, token);
      if (answer === null) return unreachable();

      if (answer.status === 200) {
        const listed = recordOf(answer.body)?.['stock'];
        if (!Array.isArray(listed)) return unreachable();

        const offers = listed.map(offerOf);

        // One unreadable offer fails the whole window: a shop drawn with a hole
        // in it is a shop a player thinks they cannot afford anything from.
        return offers.includes(null)
          ? unreachable()
          : { ok: true, value: offers as readonly VendorWindowOffer[] };
      }

      const refusal = common(answer, true);

      return refusal === null ? unreachable() : { ok: false, refusal };
    },

    buyFromVendor: async (token: string, characterId: string, offerId: string): Promise<ApiResult<Purchase>> => {
      // `parseOfferBody` refuses a body carrying anything it did not declare, so
      // the object is written out rather than spread from a row.
      const answer = await post(characterPath(characterId, '/vendor/buy'), { offerId }, token);

      return exchanged(answer, 'spent', (character, quote, spent) => ({ character, quote, spent }));
    },

    sellToVendor: async (token: string, characterId: string, itemId: string): Promise<ApiResult<Sale>> => {
      const answer = await post(characterPath(characterId, '/vendor/sell'), { itemId }, token);

      return exchanged(answer, 'received', (character, quote, received) => ({ character, quote, received }));
    },

    repairAtVendor: async (token: string, characterId: string, itemId: string): Promise<ApiResult<Purchase>> => {
      const answer = await post(characterPath(characterId, '/vendor/repair'), { itemId }, token);

      return exchanged(answer, 'spent', (character, quote, spent) => ({ character, quote, spent }));
    },
  };
};
